import React, { useEffect, useMemo, useState } from "react";
import { MainLayout } from "../layout/mainLayout";
import { showApiError } from "../framework/formUtils";
import { WorkShiftTimelineGrid } from "./WorkShiftTimelineGrid";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { WorkShiftPlanningTopBar } from "./WorkShiftPlanningTopBar";
import Box from "@mui/material/Box";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import { useVerifyOptionEnabled } from "../../hooks/useAppOption";
import { OptionRoutesEnabled } from "../../models/common/consts";
import { useParams } from "react-router-dom";
import {
    WorkShiftPlanningParams,
    WorkShiftPlanningTab,
} from "./WorkShiftPlanningRouter";
import { useAsyncThunkAction } from "../../hooks/useAsyncThunkAction";
import { fetchPlannedWorkShifts } from "../../store/workShiftPlanningSlice";
import { EmployeeListItemDto } from "../../models/employee/employee";
import { getEmployees } from "../../services/employeeServiceNew";
import { fetchColorScheme } from "../../store/colorSchemeSlice";

export const WorkShiftPlanningMain = () => {
    const dispatch = useAppDispatch();
    const { tab } = useParams<WorkShiftPlanningParams>();
    const [employees, setEmployees] = useState<EmployeeListItemDto[]>();
    const selectedEmployeeGroupId = useAppSelector(
        (state) => state.workShiftPlanning.filters.selectedEmployeeGroupId
    );
    const selectedEmployeeIds = useAppSelector(
        (state) => state.workShiftPlanning.filters.selectedEmployeeIds
    );
    const includeUnassigned = useAppSelector(
        (state) => state.workShiftPlanning.filters.showUnemployable
    );
    const visibleEmployees = useMemo(
        () =>
            employees?.filter(
                (employee) =>
                    !selectedEmployeeIds ||
                    selectedEmployeeIds.length === 0 ||
                    selectedEmployeeIds.includes(employee.id)
            ),
        [selectedEmployeeIds, employees]
    );

    useVerifyOptionEnabled(
        OptionRoutesEnabled,
        tab === WorkShiftPlanningTab.Route
    );

    const shifts = useAppSelector(
        (state) => state.workShiftPlanning.plannedWorkShifts
    );
    const loading = useAppSelector(
        (state) => state.workShiftPlanning.shiftsLoading
    );
    const reloadKey = useAppSelector(
        (state) => state.workShiftPlanning.reloadKey
    );
    const { startDate, endDate } = useAppSelector(
        (state) => state.workShiftPlanning.filters
    );

    useEffect(() => {
        getEmployees(selectedEmployeeGroupId).then((res) => setEmployees(res?.data)).catch(showApiError);
    }, [selectedEmployeeGroupId]);

    useAsyncThunkAction(
        () => {
            if (!!startDate && !!endDate) {
                return fetchPlannedWorkShifts({
                    startDate,
                    endDate,
                    employeeGroupId: selectedEmployeeGroupId || undefined,
                    employeeIds:
                        selectedEmployeeIds && selectedEmployeeIds.length > 0
                            ? selectedEmployeeIds
                            : undefined,
                    includeUnassigned,
                });
            }
        },
        { onError: showApiError },
        [
            reloadKey,
            startDate,
            endDate,
            selectedEmployeeGroupId,
            selectedEmployeeIds,
            includeUnassigned,
        ]
    );

    useEffect(() => {
        dispatch(fetchColorScheme("workShiftPlanning"));
    }, [dispatch]);

    return (
        <MainLayout
            topComponent={
                <WorkShiftPlanningTopBar
                    employees={employees}
                    visibleEmployees={visibleEmployees}
                />
            }
        >
            <Box
                sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <LoadingIndicator loading={loading} />
                {!!startDate && !!endDate ? (
                    <WorkShiftTimelineGrid
                        shifts={shifts}
                        timelineStart={startDate}
                        timelineEnd={endDate}
                        employees={visibleEmployees}
                    />
                ) : null}
            </Box>
        </MainLayout>
    );
};